import { ChangeDetectionStrategy, Component, Inject, OnInit, signal } from "@angular/core";
import { ButtonModule } from "primeng/button";
import { DynamicDialogRef } from "primeng/dynamicdialog";
import { CountrySelectComponent, countryFromBrowser } from "@zaeper/angular-dashboard-view-lib";
import { IDeveloperBillingRestClient } from "../../rest-clients/billing/i-developer-billing-rest.client";
import { developerBillingRestClientInjectionToken } from "../../rest-clients/billing/developer-billing-rest-client-injection-token";
import { LicencePriceComponent } from "../../components/licence-price/licence-price.component";
import { ZDictionaryPipe } from "@zaeper/angular-localization-lib";

/**
 * Asks the developer's billing country once, before the first payment method or the developer licence. It decides
 * the currency; Stripe fixes the currency with the first subscription. Closes with true once the customer exists.
 */
@Component({
  selector: "app-billing-country-modal",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ ButtonModule, CountrySelectComponent, LicencePriceComponent, ZDictionaryPipe ],
  template: `
    <div class="flex flex-col gap-4">
      <h3 class="text-lg font-semibold m-0">{{ "billing-country-title" | zDictionary }}</h3>
      <p class="text-sm text-surface-600 dark:text-surface-300 m-0">{{ "billing-country-intro" | zDictionary }}</p>
      <div class="flex flex-col gap-2">
        <label for="billing-country" class="font-medium">{{ "billing-country-label" | zDictionary }} *</label>
        <zaeper-country-select inputId="billing-country" [(country)]="country" [placeholder]="'billing-country-placeholder' | zDictionary" />
      </div>
      <app-licence-price [country]="country()" />
      @if (saveFailed()) {
        <div class="p-3 bg-red-50 text-red-700 rounded-lg text-sm" role="alert">{{ "billing-country-save-error" | zDictionary }}</div>
      }
      <div class="flex justify-end gap-2 pt-2">
        <p-button [label]="'billing-country-cancel' | zDictionary" [outlined]="true" [rounded]="true" (onClick)="close()" />
        <p-button [label]="'billing-country-continue' | zDictionary" [rounded]="true" [loading]="isSaving()" [disabled]="!country() || isSaving()" (onClick)="save()" />
      </div>
    </div>
  `
})
export class BillingCountryModalComponent implements OnInit {
  public readonly country = signal<string | null>( null );
  public readonly isSaving = signal( false );
  public readonly saveFailed = signal( false );

  constructor(
    @Inject(developerBillingRestClientInjectionToken) private readonly _billingRestClient: IDeveloperBillingRestClient,
    private readonly _dynamicDialogRef: DynamicDialogRef
  ) {
  }

  ngOnInit(): void {
    this.country.set( countryFromBrowser() );
  }

  public async save(): Promise<void> {
    const country = this.country();
    if ( !country ) return;
    this.isSaving.set( true );
    this.saveFailed.set( false );
    try {
      await this._billingRestClient.createCustomer( country );
      this._dynamicDialogRef.close( true );
    } catch {
      this.saveFailed.set( true );
      this.isSaving.set( false );
    }
  }

  public close(): void {
    this._dynamicDialogRef.close( false );
  }
}
